// src/components/commerce/CommerceOrders.js
// Commandes — /commerce/orders. One canonical order list per account, fed by
// the batch API, Shopify and PrestaShop; every shipment is watched by the
// parcel monitor and a parcel problem surfaces here, as a signed webhook and
// as a note on the merchant's order (Backend docs/commerce-orders.md).
// Tabs live in the URL (?tab=), so a link can open « Webhooks » directly.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import jwtDecode from 'jwt-decode';
import { FaStore, FaSync } from 'react-icons/fa';
import OrdersTab from './OrdersTab';
import ApiTab from './ApiTab';
import WebhooksTab from './WebhooksTab';
import { get } from './api';
import './CommerceOrders.css';

// Shops are connected on /shops (one place for orders, parcels and the catalogue).
const TABS = ['orders', 'api', 'webhooks'];

const isAdminToken = () => {
    try {
        return jwtDecode(localStorage.getItem('access_token') || '')?.role !== 'employee';
    } catch (e) {
        return false;
    }
};

const CommerceOrders = () => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'fr';
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'orders';
    const [stats, setStats] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const isAdmin = useMemo(isAdminToken, []);

    const loadStats = useCallback(async () => {
        try {
            const res = await get('/orders/stats');
            setStats(res.data);
        } catch (e) {
            setStats(null);
        }
    }, []);

    useEffect(() => { loadStats(); }, [loadStats, refreshKey]);

    const goTo = (next) => setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'orders') params.delete('tab'); else params.set('tab', next);
        return params;
    }, { replace: true });

    const monitoring = stats?.parcel_monitoring?.enabled;
    const bySource = stats?.by_source || {};

    if (searchParams.get('tab') === 'connections') return <Navigate to="/shops" replace />;

    return (
        <div className="co-page">
            <header className="co-header">
                <div>
                    <h1>{t('commerceOrders.title', 'Commandes')}</h1>
                    <p>{t('commerceOrders.subtitle', 'Vos commandes Shopify, PrestaShop et API au même endroit, avec l’état de chaque colis.')}</p>
                </div>
                <div className="co-header-actions">
                    <Link className="u-btn u-btn--secondary" to="/shops">
                        <FaStore aria-hidden="true" /> {t('commerceOrders.shopsLink', 'Boutiques connectées')}
                    </Link>
                    <button type="button" className="co-icon-btn" onClick={() => setRefreshKey((k) => k + 1)}
                        title={t('commerceOrders.refresh', 'Actualiser')} aria-label={t('commerceOrders.refresh', 'Actualiser')}>
                        <FaSync />
                    </button>
                </div>
            </header>

            {stats && (
                <div className="co-kpis">
                    <div className="co-kpi">
                        <span className="co-kpi-value">{(stats.total || 0).toLocaleString(lang)}</span>
                        <span className="co-kpi-label">{t('commerceOrders.kpiOrders', 'commandes')}</span>
                        <span className="co-kpi-sub">
                            {['shopify', 'prestashop', 'api'].filter((s) => bySource[s]).map((s) => `${t(`commerceOrders.sources.${s}`, s)} ${bySource[s].toLocaleString(lang)}`).join(' · ') || '—'}
                        </span>
                    </div>
                    <button type="button" className={`co-kpi co-kpi--action ${stats.problem_open ? 'co-kpi--danger' : ''}`}
                        onClick={() => goTo('orders')} disabled={!stats.problem_open}>
                        <span className="co-kpi-value">{(stats.problem_open || 0).toLocaleString(lang)}</span>
                        <span className="co-kpi-label">{t('commerceOrders.kpiProblems', 'avec un problème colis en cours')}</span>
                    </button>
                    <div className={`co-kpi ${monitoring ? '' : 'co-kpi--warning'}`}>
                        <span className="co-kpi-value co-kpi-value--text">
                            {monitoring ? t('commerceOrders.monitoringOn', 'Active') : t('commerceOrders.monitoringOff', 'Désactivée')}
                        </span>
                        <span className="co-kpi-label">{t('commerceOrders.kpiMonitoring', 'surveillance des colis')}</span>
                        <Link className="co-kpi-sub co-link" to="/parcel-monitor">
                            {monitoring ? t('commerceOrders.openMonitorShort', 'Ouvrir la surveillance') : t('commerceOrders.enableMonitoring', 'Activer pour suivre les colis des commandes')}
                        </Link>
                    </div>
                </div>
            )}

            <nav className="co-tabs" role="tablist">
                {TABS.map((key) => (
                    <button key={key} type="button" role="tab" aria-selected={tab === key}
                        className={`co-tab ${tab === key ? 'is-active' : ''}`} onClick={() => goTo(key)}>
                        {t(`commerceOrders.tabs.${key}`, key)}
                    </button>
                ))}
            </nav>

            {tab === 'orders' && <OrdersTab onGoTo={goTo} refreshKey={refreshKey} onChanged={loadStats} initialQuery={searchParams.get('q') || ''} />}
            {tab === 'api' && <ApiTab isAdmin={isAdmin} monitoring={monitoring} />}
            {tab === 'webhooks' && <WebhooksTab isAdmin={isAdmin} />}
        </div>
    );
};

export default CommerceOrders;
